import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/sections/Footer';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import PlanComparisonTable from '@/components/shared/PlanComparisonTable';
import IntervalToggle from '@/components/shared/IntervalToggle';
import { useSEO, SITE_URL } from '@/hooks/useSEO';
import { PLANS as PLAN_CATALOG } from '@/data/plans';
import {
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Loader2,
  Globe,
  User as UserIcon,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Building2,
  Tag,
  X as XIcon,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

// Per-plan extras specific to the marketing funnel (extra-domain add-on price).
// Not display copy, so they live here rather than in the shared catalog.
const EXTRA_DOMAIN_GBP = { essential: 19, professional: 29, business: 39 };

// Derive the marketing card shape from the shared catalog (single source of
// truth). Display strings (name, price, valueProp, features) come from PLAN_CATALOG;
// only the funnel-specific add-on price is merged in locally.
const PLANS = Object.entries(PLAN_CATALOG).map(([id, plan]) => ({
  id,
  name: `Acodia Watch ${plan.name}`,
  price: `£${plan.price.monthly}`,
  priceGbp: plan.price.monthly,
  priceAnnual: `£${plan.price.annual.toLocaleString('en-GB')}`,
  priceGbpAnnual: plan.price.annual,
  extraDomainGbp: EXTRA_DOMAIN_GBP[id],
  cadence: '/month',
  valueProp: plan.valueProp,
  features: plan.features,
  // Drives the subtle card highlight only — the "Most popular" badge has been
  // removed in favour of the per-plan value proposition.
  popular: Boolean(plan.mostPopular),
}));

const LEGACY_PLAN_MAP = { 1: 'essential', 3: 'professional', 5: 'business' };

function computeDiscount(basePriceGbp, applied) {
  if (!applied) return 0;
  if (applied.discount_type === 'percentage') {
    return (basePriceGbp * applied.discount_value) / 100;
  }
  return Math.min(applied.discount_value, basePriceGbp);
}

function formatGbp(n) {
  return `£${(Math.round(n * 100) / 100).toFixed(2)}`;
}

const DOMAIN_RE = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const VALID_PLAN_IDS = ['essential', 'professional', 'business'];

function normaliseDomain(raw) {
  if (!raw) return '';
  let v = raw.trim().toLowerCase();
  v = v.replace(/^https?:\/\//, '');
  v = v.split('/')[0];
  v = v.split(':')[0];
  return v;
}

function sanitiseDomain(input) {
  if (!input) return '';
  return input
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    .trim()
    .toLowerCase();
}

function isValidDomain(v) {
  if (!v) return true;
  return DOMAIN_RE.test(v);
}

function passwordStrengthError(pw) {
  if (!pw || pw.length < 8) return 'Password must be at least 8 characters';
  if (!/[0-9]/.test(pw)) return 'Password must include at least one number';
  return null;
}

function planLabel(_plan) {
  return 'Your domain to monitor';
}

export default function ActiveGuardPage() {
  useSEO({
    title: 'Acodia Watch Free Trial | Acodia — Grow. Defend. Be Found.',
    description: 'Start your 7-day free Acodia Watch trial. Continuous security monitoring from £49/month. Card required — cancel any time.',
    path: '/watch',
    ogImage: `${SITE_URL}/images/og/og-defend.png`,
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, tokens, authFetch, saveTokens, fetchUser } = useAuth();
  const isAuthed = Boolean(tokens && user);

  const initialPlan = (() => {
    const raw = (searchParams.get('plan') || '').toLowerCase();
    if (VALID_PLAN_IDS.includes(raw)) return raw;
    // Back-compat: ?plan=1|3|5 → essential|professional|business
    const legacy = parseInt(raw, 10);
    if (LEGACY_PLAN_MAP[legacy]) return LEGACY_PLAN_MAP[legacy];
    return 'professional';
  })();

  const [plan, setPlan] = useState(initialPlan);

  const initialInterval = (searchParams.get('interval') || '').toLowerCase() === 'annual'
    ? 'annual'
    : 'monthly';
  const [billingInterval, setBillingInterval] = useState(initialInterval);

  // Authenticated users convert to a paid plan via Stripe Checkout.
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);

  const startCheckout = async (planId) => {
    setCheckoutError(null);
    setCheckoutLoadingPlan(planId);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId, interval: billingInterval }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.checkout_url) {
        setCheckoutError(
          (typeof data?.detail === 'string' && data.detail) ||
          'Could not start checkout. Please try again.',
        );
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      setCheckoutError('Could not start checkout. Please try again.');
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') || user?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [authorised, setAuthorised] = useState(false);

  const [values, setValues] = useState(['', '', '', '', '']);
  const [touched, setTouched] = useState([false, false, false, false, false]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [emailExists, setEmailExists] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Promo code state — collapsed link → expanded input → applied/success, reset on remove.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // { code, discount_type, discount_value, discount_amount, label }
  const [promoError, setPromoError] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const selectedPlan = useMemo(() => PLANS.find((p) => p.id === plan) || PLANS[0], [plan]);
  const promoDiscountGbp = useMemo(
    () => computeDiscount(selectedPlan.priceGbp, promoApplied),
    [selectedPlan, promoApplied],
  );
  const promoDiscountedPriceGbp = Math.max(0, selectedPlan.priceGbp - promoDiscountGbp);

  const applyPromoCode = async () => {
    const code = (promoInput || '').trim().toUpperCase();
    if (!code) {
      setPromoError('Enter a code to apply.');
      return;
    }
    setPromoError(null);
    setPromoLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          service: 'active-guard',
          subtotal: selectedPlan.priceGbp,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.valid) {
        const detail = data?.detail;
        const msg =
          (typeof detail === 'string' && detail) ||
          (typeof detail === 'object' && detail?.error) ||
          (res.status === 404 && 'This code is not valid.') ||
          (res.status === 410 && 'This code has expired or reached its usage limit.') ||
          'This code is not valid.';
        setPromoError(msg);
        setPromoApplied(null);
        return;
      }
      setPromoApplied({
        code: data.code,
        discount_type: data.discount_type,
        discount_value: data.discount_value,
        discount_amount: data.discount_amount,
        label: data.label,
      });
      setPromoInput('');
    } catch (err) {
      setPromoError('Could not reach the promo service. Please try again.');
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setPromoApplied(null);
    setPromoError(null);
    setPromoInput('');
    setPromoOpen(false);
  };

  useEffect(() => {
    if (isAuthed && user?.email) {
      setEmail(user.email);
    }
  }, [isAuthed, user]);

  // All plans include 1 domain at the base price; extra domains are a future feature.
  const visibleCount = 1;
  const activeFields = useMemo(() => values.slice(0, visibleCount), [values, visibleCount]);

  const handleChangeValue = (i, raw) => {
    setFieldErrors((prev) => {
      if (!prev[i]) return prev;
      const next = { ...prev };
      delete next[i];
      return next;
    });
    setValues((prev) => {
      const next = [...prev];
      next[i] = raw;
      return next;
    });
  };

  const handleBlur = (i) => {
    setTouched((prev) => {
      const next = [...prev];
      next[i] = true;
      return next;
    });
    setValues((prev) => {
      const next = [...prev];
      next[i] = sanitiseDomain(prev[i]);
      return next;
    });
  };

  const changePlan = (nextPlan) => {
    setError(null);
    setFieldErrors({});
    setPlan(nextPlan);
    // All plans start with 1 domain — keep only the primary slot populated.
    setValues((prev) => prev.map((v, idx) => (idx < 1 ? v : '')));
    setTouched((prev) => prev.map((t, idx) => (idx < 1 ? t : false)));
  };

  const firstInvalidDomainIndex = useMemo(() => {
    for (let i = 0; i < visibleCount; i += 1) {
      const raw = values[i];
      const v = sanitiseDomain(raw);
      if (i === 0 && !v) return 0;
      if (v && !isValidDomain(v)) return i;
    }
    return -1;
  }, [values, visibleCount]);

  const identityValid = useMemo(() => {
    if (isAuthed) return true;
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!EMAIL_RE.test((email || '').trim())) return false;
    if (passwordStrengthError(password)) return false;
    if (password !== confirmPassword) return false;
    if (!companyName.trim()) return false;
    if (!authorised) return false;
    return true;
  }, [isAuthed, firstName, lastName, email, password, confirmPassword, companyName, authorised]);

  const canSubmit =
    firstInvalidDomainIndex === -1 &&
    sanitiseDomain(values[0]).length > 0 &&
    identityValid &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const submitted = activeFields
      .map((v) => sanitiseDomain(v))
      .filter((v) => v.length > 0);

    if (submitted.length === 0) {
      setError('Please enter at least one domain.');
      return;
    }

    setError(null);
    setEmailExists(false);
    setFieldErrors({});
    setSubmitting(true);

    const payload = { plan, domains: submitted };
    if (promoApplied?.code) {
      payload.promo_code = promoApplied.code;
    }
    if (!isAuthed) {
      Object.assign(payload, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
        password,
        confirm_password: confirmPassword,
        company_name: companyName.trim(),
        authorised: true,
        honeypot: '',
      });
    }

    // Helper: pull the most useful message out of FastAPI's various error shapes.
    const extractErrorMessage = (status, body) => {
      const d = body?.detail;
      if (!d) return `Signup failed (${status}). Please try again.`;
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) {
        // Pydantic 422 validation — [{loc, msg, type}, ...]
        const msgs = d
          .map((item) => {
            const field = Array.isArray(item?.loc)
              ? item.loc.filter((p) => p !== 'body').join('.')
              : '';
            const msg = item?.msg || 'invalid';
            return field ? `${field}: ${msg}` : msg;
          })
          .filter(Boolean);
        return msgs.length ? msgs.join(' · ') : `Signup failed (${status}).`;
      }
      if (typeof d === 'object') {
        if (d.error) return d.error;
        return JSON.stringify(d);
      }
      return `Signup failed (${status}).`;
    };

    try {
      // Anonymous path always uses plain fetch — the backend accepts no
      // Authorization header and runs the public trial signup branch.
      // Authed path uses authFetch; if the session has expired we fall back
      // to a plain fetch (which, without identity fields present, will
      // return a clear 400 that surfaces via extractErrorMessage below).
      let res;
      try {
        const doFetch = isAuthed ? authFetch : fetch;
        res = await doFetch(`${BACKEND_URL}/api/active-guard/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch (authFetchErr) {
        // authFetch throws on expired/missing session. Retry anonymously so
        // the backend returns a real HTTP response we can display.
        if (isAuthed) {
          res = await fetch(`${BACKEND_URL}/api/active-guard/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } else {
          throw authFetchErr;
        }
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn('[active-guard signup] failed', res.status, data);
        const detail = data?.detail;

        if (res.status === 409 && detail && typeof detail === 'object' && detail.code === 'email_exists') {
          setEmailExists(true);
          setError(detail.error || 'An account with this email already exists.');
          return;
        }

        if (detail && typeof detail === 'object' && Array.isArray(detail.domains)) {
          const next = {};
          detail.domains.forEach((d) => { next[d.index] = d.error; });
          setFieldErrors(next);
          setError(detail.error || 'One or more domains are invalid.');
          return;
        }

        setError(extractErrorMessage(res.status, data));
        return;
      }

      if (data?.tokens && saveTokens) {
        saveTokens(data.tokens);
        try { await fetchUser?.(); } catch { /* non-fatal */ }
      }

      const redirect = data?.redirect_url || '/dashboard?welcome=true';
      navigate(redirect, { replace: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[active-guard signup] network error', err);
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.025)',
    border: '1px solid var(--b1)',
    color: 'var(--white)',
  };

  return (
    <div
      data-testid="active-guard-page"
      style={{ background: 'var(--bg)', minHeight: '100vh', color: 'var(--white)' }}
    >
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-10 sm:pt-40 sm:pb-12 border-b"
        style={{ background: 'var(--bg)', borderColor: 'var(--b1)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: [
              'radial-gradient(ellipse 70% 50% at 55% 30%, rgba(139,92,246,0.10) 0%, transparent 60%)',
              'radial-gradient(ellipse 50% 60% at 20% 70%, rgba(74,222,128,0.05) 0%, transparent 60%)',
            ].join(', '),
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="flex items-center gap-2 mb-3" style={{ color: 'var(--a)' }}>
              <ShieldCheck size={14} />
              <p className="font-body text-xs uppercase tracking-[0.22em]">Acodia Watch · 7-day free trial</p>
            </div>
            <h1
              className="font-display font-extrabold tracking-[-0.03em] mb-4 max-w-[22ch]"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', color: 'var(--white)' }}
            >
              Start your trial. <span style={{ color: 'var(--a)' }}>Always watching.</span>
            </h1>
            <p
              className="font-body font-light text-base sm:text-lg max-w-3xl"
              style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.72' }}
            >
              Seven days free. Card required to unlock monitoring. We&rsquo;ll only charge
              your first month once the trial ends — cancel any time from your dashboard.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Plan selector + signup form */}
      <section className="py-10 sm:py-14" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} noValidate>
            {/* Choose your plan — header + Monthly/Annual toggle */}
            <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
              <div>
                <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--white)' }}>
                  Choose your plan
                </h2>
                <p className="font-body text-sm" style={{ color: 'var(--ts)' }}>
                  {isAuthed
                    ? 'Subscribe to keep Acodia Watch monitoring active.'
                    : 'Start with a 7-day free trial. Switch or cancel any time.'}
                </p>
              </div>
              <IntervalToggle interval={billingInterval} onChange={setBillingInterval} />
            </div>

            {/* Snapshot-vs-continuous comparison strip */}
            <div
              data-testid="active-guard-comparison-strip"
              className="rounded-[8px] px-5 py-4 mb-6"
              style={{
                background: 'rgba(139,92,246,0.06)',
                border: '1px solid var(--b2)',
              }}
            >
              <p className="font-body text-sm" style={{ color: 'rgba(240,237,248,0.82)', lineHeight: '1.65' }}>
                <span style={{ color: 'var(--white)', fontWeight: 600 }}>Free Acodia Scan gives you a snapshot.</span>{' '}
                Acodia Watch gives you continuous monitoring 24/7, automatic alerts when anything changes, and a
                security posture you can trust — so you can focus on running your business while we watch over yours.
              </p>
            </div>

            {/* Plan cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {PLANS.map((p) => {
                const active = plan === p.id;
                const isAnnual = billingInterval === 'annual';
                const priceMain = isAnnual ? p.priceAnnual : p.price;
                const cadence = isAnnual ? '/year' : '/month';
                const showPromo = active && promoApplied && !isAnnual && !isAuthed;
                const cardInner = (
                  <>
                    <div className="flex items-center gap-2 mb-3" style={{ color: active ? 'var(--a-bright)' : 'var(--a)' }}>
                      <ShieldCheck size={16} />
                      <span className="font-body text-xs uppercase tracking-[0.22em]">{p.id}</span>
                    </div>
                    <h3 className="font-display text-lg font-semibold mb-1" style={{ color: 'var(--white)' }}>
                      {p.name}
                    </h3>
                    <p className="font-body text-sm mb-4" style={{ color: 'rgba(240,237,248,0.75)', lineHeight: '1.6' }}>
                      {p.valueProp}
                    </p>
                    {showPromo ? (
                      <div className="mb-4">
                        <div className="flex items-baseline gap-2">
                          <span
                            className="font-display text-sm"
                            style={{ color: 'var(--ts)', textDecoration: 'line-through' }}
                          >
                            {p.price}
                          </span>
                          <span className="font-display text-3xl font-extrabold" style={{ color: 'var(--ok)' }}>
                            {formatGbp(promoDiscountedPriceGbp)}
                          </span>
                          <span className="font-body text-sm" style={{ color: 'var(--ts)' }}>{p.cadence}</span>
                        </div>
                        <span
                          data-testid={`active-guard-savings-${p.id}`}
                          className="inline-block mt-2 font-body text-xs px-2 py-0.5 rounded"
                          style={{
                            background: 'rgba(74,222,128,0.10)',
                            border: '1px solid rgba(74,222,128,0.25)',
                            color: 'var(--ok)',
                          }}
                        >
                          Save {formatGbp(promoDiscountGbp)}/month
                        </span>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <div className="flex items-baseline gap-1">
                          <span className="font-display text-3xl font-extrabold" style={{ color: 'var(--white)' }}>{priceMain}</span>
                          <span className="font-body text-sm" style={{ color: 'var(--ts)' }}>{cadence}</span>
                        </div>
                        {isAnnual ? (
                          <p className="font-body mt-1" style={{ fontSize: '0.75rem', color: 'var(--ts)' }}>
                            Billed annually · 2 months free
                          </p>
                        ) : (
                          <p
                            data-testid={`active-guard-extra-domain-${p.id}`}
                            className="font-body mt-1"
                            style={{ fontSize: '0.75rem', color: 'var(--ts)' }}
                          >
                            +£{p.extraDomainGbp}/mo per extra domain
                          </p>
                        )}
                      </div>
                    )}
                    <ul className="space-y-2">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 font-body text-sm" style={{ color: 'rgba(240,237,248,0.78)' }}>
                          <CheckCircle2 size={14} style={{ color: 'var(--ok)', marginTop: 3, flexShrink: 0 }} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </>
                );

                // Authenticated visitors convert to a paid plan via Stripe
                // Checkout — each card carries its own "Choose this plan" CTA.
                if (isAuthed) {
                  const loading = checkoutLoadingPlan === p.id;
                  return (
                    <div
                      key={p.id}
                      data-testid={`active-guard-plan-${p.id}`}
                      className="relative text-left p-6 rounded-lg flex flex-col"
                      style={{
                        background: p.popular ? 'rgba(139,92,246,0.08)' : 'var(--l1)',
                        border: p.popular ? '1px solid var(--b3)' : '1px solid var(--b1)',
                      }}
                    >
                      {cardInner}
                      <Button
                        type="button"
                        data-testid={`active-guard-choose-${p.id}`}
                        onClick={() => startCheckout(p.id)}
                        disabled={Boolean(checkoutLoadingPlan)}
                        className="btn-shimmer font-body font-medium text-sm h-11 px-5 rounded-[3px] mt-5 w-full justify-center"
                        style={{
                          background: 'var(--a)',
                          color: 'var(--white)',
                          opacity: checkoutLoadingPlan && !loading ? 0.6 : 1,
                        }}
                      >
                        {loading ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 size={14} className="animate-spin" /> Redirecting…
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            Choose this plan <ArrowRight size={14} />
                          </span>
                        )}
                      </Button>
                    </div>
                  );
                }

                // Anonymous visitors keep the existing single-select trial funnel.
                return (
                  <button
                    type="button"
                    key={p.id}
                    data-testid={`active-guard-plan-${p.id}`}
                    aria-pressed={active}
                    onClick={() => changePlan(p.id)}
                    className="relative text-left p-6 rounded-lg transition-all"
                    style={{
                      background: active ? 'rgba(139,92,246,0.08)' : 'var(--l1)',
                      border: active ? '1px solid var(--b3)' : '1px solid var(--b1)',
                      boxShadow: active ? '0 0 0 1px var(--b3) inset' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.borderColor = 'var(--b2)';
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.borderColor = 'var(--b1)';
                    }}
                  >
                    {cardInner}
                    <div
                      className="mt-5 font-body text-xs"
                      style={{ color: active ? 'var(--a-bright)' : 'var(--ts)' }}
                    >
                      {active ? 'Selected' : 'Select this plan'}
                    </div>
                  </button>
                );
              })}
            </div>

            {isAuthed && checkoutError && (
              <div
                className="mb-10 p-3 rounded flex items-start gap-2"
                style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}
              >
                <AlertCircle size={16} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
                <p className="font-body text-sm" style={{ color: 'var(--danger)' }}>{checkoutError}</p>
              </div>
            )}

            <PlanComparisonTable />

            {/* Identity block (anonymous visitors only) */}
            {!isAuthed && (
              <div
                className="p-6 sm:p-8 rounded-lg mb-6"
                style={{ background: 'var(--l1)', border: '1px solid var(--b1)' }}
              >
                <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--a)' }}>
                  <UserIcon size={14} />
                  <p className="font-body text-xs uppercase tracking-[0.22em]">Step 1 · Your account</p>
                </div>
                <p className="font-body text-sm mb-6" style={{ color: 'rgba(240,237,248,0.7)' }}>
                  We&rsquo;ll create your Acodia Watch account and log you straight into the dashboard.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>First name</label>
                    <Input
                      data-testid="ag-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Jane"
                      className="font-body text-sm h-11 rounded-[3px]"
                      style={inputStyle}
                      required
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Last name</label>
                    <Input
                      data-testid="ag-last-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className="font-body text-sm h-11 rounded-[3px]"
                      style={inputStyle}
                      required
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Work email</label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
                      <Input
                        data-testid="ag-email"
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setEmailExists(false); }}
                        placeholder="you@company.com"
                        className="font-body text-sm h-11 rounded-[3px] pl-9"
                        style={inputStyle}
                        required
                      />
                    </div>
                    {emailExists && (
                      <p className="font-body text-xs mt-1.5" style={{ color: 'var(--danger)' }}>
                        An account with this email already exists.{' '}
                        <a
                          href={`/login?next=${encodeURIComponent('/watch?plan=' + plan)}`}
                          className="underline"
                          style={{ color: 'var(--a)' }}
                        >
                          Sign in instead
                        </a>.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Password</label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
                      <Input
                        data-testid="ag-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Min 8 chars, 1 number"
                        className="font-body text-sm h-11 rounded-[3px] pl-9 pr-10"
                        style={inputStyle}
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--te)' }}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {password && passwordStrengthError(password) && (
                      <p className="font-body text-xs mt-1.5" style={{ color: 'var(--danger)' }}>
                        {passwordStrengthError(password)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Confirm password</label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
                      <Input
                        data-testid="ag-confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        className="font-body text-sm h-11 rounded-[3px] pl-9 pr-10"
                        style={inputStyle}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--te)' }}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {confirmPassword && confirmPassword !== password && (
                      <p className="font-body text-xs mt-1.5" style={{ color: 'var(--danger)' }}>
                        Passwords don&rsquo;t match.
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Company name</label>
                    <div className="relative">
                      <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
                      <Input
                        data-testid="ag-company"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Acme Ltd"
                        className="font-body text-sm h-11 rounded-[3px] pl-9"
                        style={inputStyle}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Domains panel — anonymous trial signup only */}
            {!isAuthed && (
            <div
              className="p-6 sm:p-8 rounded-lg"
              style={{ background: 'var(--l1)', border: '1px solid var(--b1)' }}
            >
              <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--a)' }}>
                <Globe size={14} />
                <p className="font-body text-xs uppercase tracking-[0.22em]">
                  {isAuthed ? 'Step 1' : 'Step 2'} · Your domains
                </p>
              </div>
              <label className="font-display text-lg font-semibold block mb-1" style={{ color: 'var(--white)' }}>
                {planLabel(plan)}
              </label>
              <p className="font-body text-sm mb-6" style={{ color: 'rgba(240,237,248,0.7)' }}>
                Only Domain 1 is required. Additional slots are optional — you can add them later from the dashboard.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {values.map((v, i) => {
                  const isVisible = i < visibleCount;
                  const required = i === 0;
                  const sanitised = sanitiseDomain(v);
                  const showFormatError =
                    isVisible && touched[i] && sanitised.length > 0 && !isValidDomain(sanitised);
                  const showRequiredError =
                    isVisible && touched[i] && required && sanitised.length === 0;
                  const serverError = fieldErrors[i];
                  const invalid = showFormatError || showRequiredError || Boolean(serverError);

                  return (
                    <div
                      key={i}
                      className="ag-field"
                      style={{
                        display: isVisible ? 'block' : 'none',
                        opacity: isVisible ? 1 : 0,
                        transition: 'opacity 280ms ease',
                      }}
                    >
                      <label
                        htmlFor={`ag-domain-${i}`}
                        className="font-body text-xs mb-1.5 block"
                        style={{ color: 'var(--ts)' }}
                      >
                        Domain {i + 1}{required ? ' (required)' : ' (optional)'}
                      </label>
                      <Input
                        id={`ag-domain-${i}`}
                        data-testid={`ag-domain-${i + 1}`}
                        value={v}
                        onChange={(e) => handleChangeValue(i, e.target.value)}
                        onBlur={() => handleBlur(i)}
                        placeholder={i === 0 ? 'yoursite.com' : `domain-${i + 1}.com (optional)`}
                        className="font-mono text-sm h-11 rounded-[3px]"
                        style={{
                          background: 'rgba(255,255,255,0.025)',
                          border: `1px solid ${invalid ? 'var(--danger)' : 'var(--b1)'}`,
                          color: 'var(--white)',
                        }}
                      />
                      <p
                        className="font-body mt-1.5"
                        style={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', color: 'var(--ts)' }}
                      >
                        Enter your domain with or without https:// or www.
                      </p>
                      {showRequiredError && (
                        <p className="font-body text-xs mt-1.5" style={{ color: 'var(--danger)' }}>
                          Please enter your primary domain.
                        </p>
                      )}
                      {showFormatError && (
                        <p className="font-body text-xs mt-1.5" style={{ color: 'var(--danger)' }}>
                          That does not look like a valid domain. Example: example.com
                        </p>
                      )}
                      {serverError && (
                        <p className="font-body text-xs mt-1.5" style={{ color: 'var(--danger)' }}>
                          {serverError}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Promo code — collapsible panel */}
              <div className="mt-6" data-testid="ag-promo-block">
                {!promoOpen && !promoApplied && (
                  <button
                    type="button"
                    data-testid="ag-promo-toggle"
                    onClick={() => { setPromoOpen(true); setPromoError(null); }}
                    className="inline-flex items-center gap-1.5 font-body underline-offset-2 hover:underline"
                    style={{ fontSize: '0.85rem', color: 'var(--ts)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <Tag size={12} />
                    Have a promo code?
                  </button>
                )}

                <div
                  style={{
                    maxHeight: promoOpen || promoApplied ? '260px' : '0px',
                    overflow: 'hidden',
                    transition: 'max-height 300ms ease',
                  }}
                >
                  {promoApplied ? (
                    <div
                      data-testid="ag-promo-applied"
                      className="flex items-start gap-3 p-3 rounded"
                      style={{
                        background: 'rgba(74,222,128,0.08)',
                        border: '1px solid rgba(74,222,128,0.25)',
                      }}
                    >
                      <CheckCircle2 size={16} style={{ color: 'var(--ok)', marginTop: 2, flexShrink: 0 }} />
                      <div className="flex-1">
                        <p className="font-body text-sm" style={{ color: 'var(--ok)' }}>
                          Code applied
                        </p>
                        <p className="font-body text-xs mt-0.5" style={{ color: 'rgba(240,237,248,0.75)' }}>
                          <span className="font-mono" style={{ color: 'var(--white)' }}>{promoApplied.code}</span>
                          {' — '}
                          {promoApplied.label} applied
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid="ag-promo-remove"
                        onClick={removePromoCode}
                        aria-label="Remove promo code"
                        className="flex items-center justify-center rounded"
                        style={{
                          width: 24,
                          height: 24,
                          color: 'var(--ts)',
                          border: '1px solid var(--b1)',
                          background: 'transparent',
                        }}
                      >
                        <XIcon size={12} />
                      </button>
                    </div>
                  ) : promoOpen ? (
                    <div>
                      <label
                        className="font-body mb-1.5 block"
                        style={{ fontSize: '0.85rem', color: 'var(--tm)' }}
                      >
                        Promo Code
                      </label>
                      <div className="flex gap-3">
                        <Input
                          data-testid="ag-promo-input"
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value); setPromoError(null); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromoCode(); } }}
                          placeholder="Enter code"
                          className="font-mono text-sm h-11 rounded-[3px] uppercase flex-1"
                          style={inputStyle}
                          autoCapitalize="characters"
                          autoCorrect="off"
                        />
                        <Button
                          type="button"
                          data-testid="ag-promo-apply"
                          onClick={applyPromoCode}
                          disabled={promoLoading || !promoInput.trim()}
                          className="font-body font-medium text-sm h-11 px-5 rounded-[3px]"
                          style={{
                            background: 'transparent',
                            border: '1px solid var(--b2)',
                            color: 'var(--white)',
                            opacity: promoLoading || !promoInput.trim() ? 0.6 : 1,
                          }}
                        >
                          {promoLoading ? (
                            <span className="inline-flex items-center gap-2">
                              <Loader2 size={14} className="animate-spin" />
                              Applying
                            </span>
                          ) : 'Apply'}
                        </Button>
                      </div>
                      {promoError && (
                        <p
                          data-testid="ag-promo-error"
                          className="font-body text-xs mt-2 inline-flex items-center gap-1.5"
                          style={{ color: 'var(--danger)' }}
                        >
                          <XIcon size={12} />
                          {promoError}
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              {!isAuthed && (
                <label className="flex items-start gap-3 mt-6 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ag-authorise"
                    checked={authorised}
                    onChange={(e) => setAuthorised(e.target.checked)}
                    className="mt-1"
                    style={{ accentColor: 'var(--a)' }}
                  />
                  <span className="font-body text-xs" style={{ color: 'rgba(240,237,248,0.7)', lineHeight: '1.6' }}>
                    I am authorised to enrol these domains in Acodia Watch monitoring and agree to the{' '}
                    <a href="/terms" className="underline" style={{ color: 'var(--a)' }}>Terms of Service</a> and{' '}
                    <a href="/privacy" className="underline" style={{ color: 'var(--a)' }}>Privacy Policy</a>.
                  </span>
                </label>
              )}

              {error && (
                <div
                  className="mt-6 p-3 rounded flex items-start gap-2"
                  style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}
                >
                  <AlertCircle size={16} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
                  <p className="font-body text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-4 mt-8">
                <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                  {isAuthed ? (
                    <>Signed in as <span style={{ color: 'var(--white)' }}>{user?.email}</span>.</>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <a
                        href={`/login?next=${encodeURIComponent('/watch?plan=' + plan)}`}
                        className="underline"
                        style={{ color: 'var(--a)' }}
                      >
                        Sign in
                      </a>.
                    </>
                  )}
                </p>
                <Button
                  type="submit"
                  data-testid="active-guard-submit"
                  disabled={!canSubmit}
                  className="btn-shimmer font-body font-medium text-sm h-12 px-6 rounded-[3px]"
                  style={{
                    background: canSubmit ? 'var(--a)' : 'var(--l3)',
                    color: 'var(--white)',
                    opacity: canSubmit ? 1 : 0.6,
                  }}
                >
                  {submitting ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Creating your account…
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      Start 7-day free trial — {selectedPlan.name.replace('Acodia Watch ', '')} <ArrowRight size={14} />
                    </span>
                  )}
                </Button>
              </div>
            </div>
            )}
          </form>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes agFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .ag-field { animation: agFadeIn 260ms ease both; }
        @keyframes agSpin { to { transform: rotate(360deg); } }
        .animate-spin { animation: agSpin 1s linear infinite; }
      `}</style>
    </div>
  );
}
